/**
 * Cart Drawer Fix:
 * 1. Prevents duplicate add-to-cart submissions (stops double reflection in cart drawer).
 * 2. Prevents unexpected redirect to checkout (/checkout) on add-to-cart.
 * 3. Smoothly opens the cart drawer and updates cart content via AJAX.
 */
(function () {
  'use strict';

  let isProcessing = false;
  let lastSubmissionTime = 0;
  const SUBMIT_COOLDOWN_MS = 1000;

  // Intercept any unwanted programmatic redirects to /checkout immediately after add-to-cart
  let preventCheckoutRedirectUntil = 0;

  // Intercept beforeunload if a redirect to checkout is attempted during cooldown
  window.addEventListener(
    'beforeunload',
    function (e) {
      if (Date.now() < preventCheckoutRedirectUntil) {
        // If an external script is forcing a navigation right after add to cart, cancel it
        e.preventDefault();
        return '';
      }
    },
    { capture: true }
  );

  /**
   * Determine if an element or form is related to adding a product to the cart.
   */
  function isAddToCartTarget(form) {
    if (!form || !(form instanceof HTMLFormElement)) return false;

    const action = form.getAttribute('action') || '';
    if (action.includes('/cart/add')) return true;

    if (form.dataset.type === 'add-to-cart-form') return true;
    if (form.classList.contains('shopify-product-form')) return true;

    if (form.querySelector('[name="add"], .add-to-cart-button, [data-add-to-cart]')) {
      return true;
    }

    return false;
  }

  /**
   * Main handler to intercept and process add-to-cart submissions cleanly.
   */
  async function handleAddToCart(form, originalEvent) {
    const now = Date.now();
    if (isProcessing || now - lastSubmissionTime < SUBMIT_COOLDOWN_MS) {
      if (originalEvent) {
        originalEvent.preventDefault();
        originalEvent.stopPropagation();
        originalEvent.stopImmediatePropagation();
      }
      return;
    }

    isProcessing = true;
    lastSubmissionTime = now;
    // Set a 3-second guard against checkout redirect
    preventCheckoutRedirectUntil = now + 3000;

    if (originalEvent) {
      originalEvent.preventDefault();
      originalEvent.stopPropagation();
      originalEvent.stopImmediatePropagation();
    }

    // Indicate loading state on button
    const submitBtn = form.querySelector('[type="submit"], [name="add"], .add-to-cart-button');
    if (submitBtn) {
      submitBtn.setAttribute('disabled', 'true');
      submitBtn.classList.add('loading');
    }

    try {
      const formData = new FormData(form);

      // Collect section IDs to update so Shopify returns updated drawer HTML
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = new Set();
      cartItemsComponents.forEach((item) => {
        if (item.dataset.sectionId) {
          sectionIds.add(item.dataset.sectionId);
        }
      });

      // Prepare request payload
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity') || '1', 10);

      if (!variantId) {
        console.warn('[Cart Drawer Fix] No variant ID found on form.');
        return;
      }

      const bodyData = {
        id: variantId,
        quantity: quantity,
      };

      if (sectionIds.size > 0) {
        bodyData.sections = Array.from(sectionIds).join(',');
        bodyData.sections_url = window.location.pathname;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(bodyData),
      });

      const responseData = await response.json();

      if (responseData.status && responseData.message) {
        console.error('[Cart Drawer Fix] Cart add error:', responseData.message);
        // Show error message if available
        const errorEl = form.querySelector('[ref="addToCartTextError"], .product-form-text__error');
        if (errorEl) {
          errorEl.textContent = responseData.message;
          errorEl.classList.remove('hidden');
        }
        return;
      }

      // Fetch the latest cart state to ensure 100% accurate count
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();

      // Update cart bubble counts across header and drawer
      updateCartBubbles(cart.item_count);

      // Update cart-items-component inside drawer if section HTML returned or via fetch
      await updateCartDrawerContent(sectionIds, responseData.sections);

      // Smoothly open the cart drawer
      openCartDrawer();

      // Dispatch single cart update event for any theme components
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              itemCount: cart.item_count,
              source: 'cart-drawer-fix',
            },
          },
        })
      );
    } catch (err) {
      console.error('[Cart Drawer Fix] Error adding to cart:', err);
    } finally {
      if (submitBtn) {
        submitBtn.removeAttribute('disabled');
        submitBtn.classList.remove('loading');
      }
      setTimeout(() => {
        isProcessing = false;
      }, 500);
    }
  }

  /**
   * Updates all cart bubble count indicators on the page.
   */
  function updateCartBubbles(count) {
    const bubbleCounts = document.querySelectorAll(
      '[ref="cartBubbleCount"], .cart-bubble__count, .cart-bubble__text, .cart-count'
    );
    bubbleCounts.forEach((el) => {
      el.textContent = count;
      if (count > 0) {
        el.classList.remove('hidden', 'visually-hidden');
      } else {
        el.classList.add('hidden');
      }
    });

    const bubbles = document.querySelectorAll('[ref="cartBubble"], .cart-bubble');
    bubbles.forEach((el) => {
      if (count > 0) {
        el.classList.remove('hidden', 'visually-hidden');
      }
    });

    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(count),
          timestamp: Date.now(),
        })
      );
    } catch (_) {}
  }

  /**
   * Updates the HTML of cart-items-component in the drawer.
   */
  async function updateCartDrawerContent(sectionIds, returnedSections) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    for (const comp of cartItemsComponents) {
      const sectionId = comp.dataset.sectionId;
      if (!sectionId) continue;

      let sectionHTML = returnedSections?.[sectionId];
      if (!sectionHTML) {
        try {
          const res = await fetch(`/?section_id=${sectionId}`);
          sectionHTML = await res.text();
        } catch (e) {
          console.warn('[Cart Drawer Fix] Section fetch failed:', e);
        }
      }

      if (sectionHTML) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHTML, 'text/html');
        const newComp = doc.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
        if (newComp) {
          comp.innerHTML = newComp.innerHTML;
        } else {
          const innerWrapper = doc.querySelector('.cart-items__wrapper, .cart-drawer__inner');
          if (innerWrapper) {
            comp.innerHTML = innerWrapper.innerHTML;
          }
        }
      }
    }
  }

  /**
   * Opens the theme's cart drawer component.
   */
  function openCartDrawer() {
    const drawerComponent = document.querySelector('cart-drawer-component');
    if (drawerComponent) {
      if (typeof drawerComponent.open === 'function') {
        drawerComponent.open();
        return;
      }
      if (typeof drawerComponent.showDialog === 'function') {
        drawerComponent.showDialog();
        return;
      }
    }

    const dialog = document.querySelector('.cart-drawer dialog, cart-drawer-component dialog');
    if (dialog && typeof dialog.showModal === 'function') {
      if (!dialog.open) {
        dialog.showModal();
      }
    }
  }

  // 1. Intercept form submit in CAPTURE phase so no native submit / checkout redirect occurs
  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (isAddToCartTarget(form)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        handleAddToCart(form, event);
      }
    },
    true
  );

  // 2. Intercept button click on Add to Cart to guarantee event.preventDefault
  document.addEventListener(
    'click',
    function (event) {
      const target = event.target;
      const atcButton = target.closest(
        'button[name="add"], .add-to-cart-button, [data-add-to-cart], .quick-add__button--add'
      );

      if (atcButton) {
        const form = atcButton.closest('form');
        if (form && isAddToCartTarget(form)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          handleAddToCart(form, event);
        }
      }
    },
    true
  );
})();
